<template>
  <el-dialog
    title="Edit Activity"
    :visible.sync="show"
    @close="$emit('update:isVisible', false)"
    :close-on-click-modal="false"
  >
    <el-row style="margin-bottom: 10px">
      <el-col style="display: flex; justify-content: space-between">
        <el-radio-group
          v-model="type"
          @change="
            selection = null;
            checkValid = false;
          "
          style="margin-bottom: 10px"
          :disabled="envRights === 'Run'"
        >
          <el-radio label="Task">DataMaestro Tasks</el-radio>
          <el-radio label="Process">TurboIntegrator Processes</el-radio>
        </el-radio-group>
        <el-checkbox
          v-model="viewControlObjects"
          @change="viewControlObjectsUpdated"
          :disabled="envRights === 'Run'"
          >View Control Objects</el-checkbox
        >
      </el-col>
    </el-row>
    <el-select
      filterable
      placeholder
      style="width: 100%"
      v-model="$v.selection.$model"
      value-key="name"
      v-if="type === 'Task'"
      :class="{ error: checkValid && $v.selection.$invalid }"
      :disabled="envRights === 'Run'"
    >
      <el-option
        v-for="task in tasks"
        :key="task.name"
        :label="task.name"
        :value="task"
      />
    </el-select>
    <el-select
      filterable
      placeholder
      style="width: 100%"
      v-model="$v.selection.$model"
      value-key="name"
      v-else
      :class="{ error: checkValid && $v.selection.$invalid }"
      :disabled="envRights === 'Run'"
    >
      <el-option
        v-for="process in processes"
        :key="process.name"
        :label="process.name"
        :value="process"
      />
    </el-select>
    <div
      v-if="
        type === 'Process' &&
        selection &&
        selection.parameters &&
        selection.parameters.length > 0
      "
      style="margin-top: 20px"
    >
      <span style="font-size: 14px; font-weight: 500">Process Parameters</span>
      <el-form style="margin-top: 10px" :disabled="envRights === 'Run'">
        <el-form-item
          v-for="({ Prompt, Value }, index) in selection.parameters"
          :key="index"
          :label="Prompt"
        >
          <el-input
            v-model="$v.selection.$model.parameters[index].Value"
            :placeholder="Value"
          />
        </el-form-item>
      </el-form>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button class="button" @click="handleSubmit">Save</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import TaskService from "../../services/TaskService";
import JobService from "../../services/JobService";
import { required } from "vuelidate/lib/validators";
import Helper from "../../utilities/Helper";
import { cloneDeep } from "lodash";

/**
 * @vue-prop {Object} row - Table row of the selected activity
 * @vue-data {Number} [index=null] - Index of the selected activity
 * @vue-data {String} [type=null] - Type of the selected activity
 * @vue-data {String} [selection=null] - Selected activity
 * @vue-data {Array} [tasks=null] - List of available tasks
 * @vue-data {Array} [processes=null] - List of PA server processes
 * @vue-data {Boolean} [checkValid=false] - Flag that indicates whether to check for errors
 * @vue-computed {Helper} mapState - Maps Vuex state to computed properties
 */
export default {
  props: {
    isVisible: Boolean,
    activityEdit: Object,
  },
  data() {
    return {
      activity: this.activityEdit,
      index: null,
      type: null,
      selection: null,
      tasks: [],
      processes: [],
      processesHidden: [],
      checkValid: false,
      show: this.isVisible,
      viewControlObjects: true,
    };
  },
  watch: {
    activityEdit(value) {
      this.activity = value;
    },
    isVisible(value) {
      this.show = value;
      if (value) {
        this.resetModal();
      } else {
        this.checkValid = false;
      }
    },
  },
  computed: {
    ...mapState(["environmentId", "instanceId", "envRights"]),
  },
  methods: {
    handleError: Helper.handleError,
    /**
     * Shows or hides the control objects
     * @function
     */
    async viewControlObjectsUpdated() {
      [this.processes, this.processesHidden] = [
        this.processesHidden,
        this.processes,
      ];
    },
    /**
     * Fetches the lists of tasks and TurboIntegrator processes
     * @function
     */
    async getOptions() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        this.tasks = [];
        this.processes = [];
        const taskResponse = await TaskService.show({
          instanceId: this.instanceId,
        });
        const processResponse = await JobService.getProcesses({
          instanceId: this.instanceId,
        });
        taskResponse.data.tasks.forEach((task) =>
          this.tasks.push({
            id: task.id,
            name: task.name,
          })
        );
        processResponse.data.processes.value.forEach((process) =>
          this.processes.push({
            name: process.Name,
            source: process.DataSource.Type,
            parameters: process.Parameters,
          })
        );
        this.processesHidden = this.processes.filter(
          (process) => process.name.charAt(0) !== "}"
        );
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Resets the modal
     */
    resetModal() {
      const tempActivity = cloneDeep(this.activity);
      this.index = tempActivity.index;
      this.type = tempActivity.type;
      switch (this.type) {
        case "Task":
          this.selection = {
            id: tempActivity.taskId,
            name: tempActivity.name,
          };
          break;
        case "Process":
          this.selection = {
            name: tempActivity.name,
            source: tempActivity.source,
            parameters: tempActivity.parameters,
          };
          break;
      }

      if (!this.viewControlObjects) this.viewControlObjectsUpdated();
      this.viewControlObjects = true;
    },
    handleSubmit() {
      this.checkValid = true;
      if (this.$v.selection.$invalid) return;

      switch (this.type) {
        case "Task":
          this.$emit("activitySubmitted", {
            type: this.type,
            selection: this.selection.id,
          });
          break;
        case "Process":
          this.$emit("activitySubmitted", {
            type: this.type,
            selection: {
              name: this.selection.name,
              source: this.selection.source,
              parameters: this.selection.parameters,
            },
          });
          break;
      }

      this.checkValid = false;
      this.$emit("update:isVisible", false);
    },
  },
  mounted() {
    this.getOptions();
  },
  validations() {
    return {
      selection:
        this.type === "Task"
          ? {
              id: { required },
              name: { required },
            }
          : {
              name: { required },
              source: { required },
            },
    };
  },
};
</script>

<style scoped>
.error :deep(.el-input__inner) {
  border: 1px solid red;
  border-radius: 4px;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button {
  background-color: #7a7c80;
  color: white;
  border-color: white;
}
.cancel-button:focus {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button:hover {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
</style>
